import { useMemo } from 'react';
import drop from 'lodash/drop';
import get from 'lodash/get';

import {
  UseLocationScheduleParams,
  UseLocationScheduleListOnlyReturns,
  LocationWeekdayWorkingHours,
  ScheduleListItem,
  WEEKDAY_SEQUENCE,
  WEEKDAY_SHORT,
} from './location-schedule.types';
import { getDefaultDayScheduleStructure } from './location-schedule.utils';

export const validateScheduleSequence = (
  workingHours: LocationWeekdayWorkingHours[],
): (LocationWeekdayWorkingHours | null)[] =>
  WEEKDAY_SEQUENCE.map((day) => workingHours.find((item) => item.dayOfWeek === day) || null);

export const generateScheduleList = (
  workingHours: (LocationWeekdayWorkingHours | null)[],
): ScheduleListItem[] => {
  const scheduleList: ScheduleListItem[] = [];
  const first = workingHours[0];
  const workingHoursRest = drop(workingHours, 1);

  let current: ScheduleListItem = first
    ? getDefaultDayScheduleStructure({
        dayOfWeek: first.dayOfWeek,
        periodStartTitle: WEEKDAY_SHORT[first.dayOfWeek],
        day: first,
      })
    : {
        periodStart: WEEKDAY_SEQUENCE[0],
        periodStartTitle: WEEKDAY_SHORT.MONDAY,
        periodEnd: null,
        periodEndTitle: null,
        open: null,
        closed: { closed: true },
      };

  workingHoursRest.forEach((item, index) => {
    const dayOfWeek = WEEKDAY_SEQUENCE[index + 1];
    const periodEndTitle = WEEKDAY_SHORT[dayOfWeek];
    const isClosed = item === null && current.closed;

    const isTodayScheduleSameAsPreviousDay =
      isClosed ||
      (item !== null &&
        current.open &&
        current.open.opens === item.openTime &&
        current.open.closes === item.closeTime);

    if (isClosed || isTodayScheduleSameAsPreviousDay) {
      current.periodEnd = dayOfWeek;
      current.periodEndTitle = periodEndTitle;
      return;
    }

    if (item === null && current.open) {
      scheduleList.push(current);
      current = {
        periodStart: dayOfWeek,
        periodStartTitle: periodEndTitle,
        periodEnd: null,
        periodEndTitle: null,
        open: null,
        closed: { closed: true },
      };
      return;
    }

    if (item !== null && current.closed) {
      scheduleList.push(current);

      current = getDefaultDayScheduleStructure({
        dayOfWeek,
        periodStartTitle: periodEndTitle,
        day: item,
      });

      return;
    }

    if (item !== null && current.open) {
      scheduleList.push(current);
      current = getDefaultDayScheduleStructure({
        dayOfWeek,
        periodStartTitle: periodEndTitle,
        day: item,
      });
    }
  });

  scheduleList.push(current);

  return scheduleList;
};

export const useLocationScheduleListOnly = (
  params: UseLocationScheduleParams,
): UseLocationScheduleListOnlyReturns => {
  const { workingHours } = params;
  return useMemo<UseLocationScheduleListOnlyReturns>(() => {
    const list = generateScheduleList(validateScheduleSequence(workingHours));
    const is24h = (list.length === 1 && get(list, '[0].open.is24h')) || false;
    return {
      list,
      is24h,
    };
  }, [workingHours]);
};
